import clsx from 'clsx';
import { DropdownMenu } from 'radix-ui';
import { MoreHorizontal } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

/* -------------------------------------------------------------------------
   ResourceTabs

   Sections of one resource. A tab that is not built yet is rendered
   `disabled` — the real attribute, not a class that looks like one — so it is
   removed from the tab order and announced as unavailable rather than silently
   doing nothing when a keyboard user activates it.

   The unavailable reason is deliberately not a milestone name. "Arrives in
   VC-3" is a sentence that goes stale the moment a plan changes, and stale copy
   in production is the defect class this project keeps finding.
   ------------------------------------------------------------------------- */

export interface ResourceTab {
  key: string;
  label: string;
  /** Omitted for a tab that is not navigable yet. */
  href?: string;
  current?: boolean;
  /** Present ⇒ the tab is disabled, and this says why in plain words. */
  unavailableReason?: string;
}

export interface ResourceTabsProps {
  tabs: readonly ResourceTab[];
  /** The router's Link. Kept injectable so this package stays router-agnostic. */
  linkComponent?: ComponentType<{
    to: string;
    className?: string;
    children: ReactNode;
    'aria-current'?: 'page';
  }>;
  label: string;
  className?: string;
}

const TAB_BASE =
  'relative -mb-px inline-flex items-center gap-1.5 border-b-2 px-0.5 pb-2.5 text-[13px] ' +
  'font-medium transition-colors duration-[var(--vc-duration-fast)] ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--vc-focus-ring)]';

export function ResourceTabs({ tabs, linkComponent, label, className }: ResourceTabsProps) {
  const Link = linkComponent;

  return (
    <nav
      aria-label={label}
      className={clsx('flex gap-6 border-b border-[var(--vc-border)]', className)}
    >
      {tabs.map((tab) => {
        if (tab.unavailableReason !== undefined || tab.href === undefined || Link === undefined) {
          return (
            <button
              key={tab.key}
              type="button"
              disabled
              title={tab.unavailableReason}
              /*
                Muted, not subtle.

                WCAG exempts disabled controls from contrast, and at
                `--vc-text-subtle` these measured 4.19:1 on the page background.
                But a tab label is information — it tells you the concept exists
                and where it will live — and "you are allowed not to read this"
                is not the same as "this is unreadable". The exemption is about
                controls you cannot use, not about words you still have to.
              */
              className={clsx(
                TAB_BASE,
                'cursor-not-allowed border-transparent text-[var(--vc-text-muted)]',
              )}
            >
              {tab.label}
            </button>
          );
        }

        return (
          <Link
            key={tab.key}
            to={tab.href}
            {...(tab.current ? { 'aria-current': 'page' as const } : {})}
            className={clsx(
              TAB_BASE,
              tab.current
                ? 'border-[var(--vc-accent)] text-[var(--vc-text)]'
                : 'border-transparent text-[var(--vc-text-muted)] hover:border-[var(--vc-border-strong)] hover:text-[var(--vc-text)]',
            )}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

/* -------------------------------------------------------------------------
   ActionMenu

   Secondary actions on a resource. Archiving lives here rather than beside
   Edit: it is rare, it is consequential, and a control the same size and weight
   as the one next to it invites the wrong click.
   ------------------------------------------------------------------------- */

export interface ActionMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** Styles the item as consequential. Never the only signal — the label says it too. */
  destructive?: boolean;
  disabled?: boolean;
}

const MENU_SURFACE =
  'z-50 min-w-[13rem] rounded-[var(--vc-radius-md)] border border-[var(--vc-border)] ' +
  'bg-[var(--vc-surface-raised)] p-1 shadow-lg';

export function ActionMenu({
  items,
  label,
  align = 'end',
}: {
  items: readonly ActionMenuItem[];
  /** Accessible name for the trigger. "More actions for JurisMind", not "More". */
  label: string;
  align?: 'start' | 'end';
}) {
  if (items.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={label}
        className={
          'inline-flex h-8 items-center justify-center gap-1 rounded-[var(--vc-radius-md)] ' +
          'border border-[var(--vc-border-strong)] bg-[var(--vc-surface)] px-2 ' +
          'text-[var(--vc-text)] transition-colors duration-[var(--vc-duration-fast)] ' +
          'hover:bg-[var(--vc-surface-hover)] ' +
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--vc-focus-ring)] ' +
          'data-[state=open]:bg-[var(--vc-surface-hover)]'
        }
      >
        <MoreHorizontal aria-hidden="true" className="size-4" />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className={MENU_SURFACE} sideOffset={6} align={align}>
          {items.map((item) => (
            <DropdownMenu.Item
              key={item.key}
              disabled={item.disabled}
              onSelect={item.onSelect}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded-[var(--vc-radius-sm)] px-2 py-1.5',
                'text-[13px] outline-none select-none',
                'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                item.destructive
                  ? 'text-[var(--vc-danger-text)] data-[highlighted]:bg-[var(--vc-danger-bg)]'
                  : 'text-[var(--vc-text)] data-[highlighted]:bg-[var(--vc-surface-hover)]',
              )}
            >
              {item.icon ? (
                <span aria-hidden="true" className="shrink-0 [&>svg]:size-3.5">
                  {item.icon}
                </span>
              ) : null}
              {item.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
